import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import moment from 'moment-timezone';
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,  
  Input,
  Select,
  Pagination,
  Col,
  Row,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import 'jspdf-autotable';
const { Option } = Select;




const DataTable = ({dataSource, columns,sumColumns=[], ...rest}) => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredData, setFilteredData] = useState(dataSource);
  const [currentRecords, setCurrentRecords] = useState(dataSource);
  const [columnTotals, setColumnTotals] = useState({});
  const [statusFilter, setStatusFilter] = useState('All');
  const [orderFilter, setOrderFilter] = useState('All');

  const calculateColumnTotals = (records) => {
    const totals = {};        
    if(records)
    {      
      sumColumns.forEach((columnName) => {    
        const total = records.reduce((sum, record) => sum + (parseFloat(record[columnName]) || 0), 0);
          totals[columnName] = total.toFixed(2);      
      });    
    }
    
    setColumnTotals(totals);
  };

  const getPagination = useCallback((currentPage, pageSize, filteredRecords) => {
    const pageStart = (currentPage - 1) * pageSize + 1;
    const pageEnd =
      currentPage * pageSize > filteredRecords.length
        ? filteredRecords.length
        : currentPage * pageSize;
  
    return { pageStart:filteredRecords.length > 0 ? pageStart : 0 , pageEnd };
  }, []);

  const [pageData, setPageData] = useState(
    getPagination(currentPage, pageSize, dataSource)
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);    
  };

  const searchTableData = (searchString, data) => {
    const filteredData = data.filter((item) =>
      Object.values(item).some((value) => {
        return value?.toString()?.toLowerCase()?.includes(searchString?.toString()?.toLowerCase());
      })
    );
    return filteredData;
  };

  const filterByStatus = (data, status) => {
    if (status === 'Active') {
      return data.filter(item => item.status === 1);
    }
    if (status === 'Inactive') {
      return data.filter(item => item.status === 0);
    }
    return data; // 'All' status
  };

  const filterByOrderStatus = (data, orderStatus) => {
    if (orderStatus === 'All') {
      return data; // No filtering
    }
    return data.filter(item => item.orderStatus === parseInt(orderStatus));
  };

  useEffect(() => {
    if (!dataSource || dataSource.length === 0) {
      return;
    }
    let filteredRecordsFromSearch = searchTableData(searchText, dataSource);
    filteredRecordsFromSearch = filterByStatus(filteredRecordsFromSearch, statusFilter);
    filteredRecordsFromSearch = filterByOrderStatus(filteredRecordsFromSearch, orderFilter);
    const currentRecords = filteredRecordsFromSearch.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );    
    setCurrentRecords(currentRecords);    
    calculateColumnTotals(currentRecords);
    setFilteredData(filteredRecordsFromSearch);
    const pageData = getPagination(
      currentPage,
      pageSize,
      filteredRecordsFromSearch
    );
    setPageData(pageData);
  }, [dataSource, searchText, currentPage, pageSize, statusFilter, orderFilter]);



  const getCsvData = () => {
    const csvData = filteredData.map((item) => {
      const row = {};
      columns
        .filter((col) => col.key !== "action" && col.title !== "Action" && col.dataIndex)
        .forEach((column) => {
          let val = item[column.dataIndex];
          const k = column.dataIndex?.toLowerCase() || "";
          const isTimeField = k.includes("createdat") || k.includes("updatedat") || k.includes("time");
          if (column.dataIndex === "status") {
            val = val === 1 || val === true ? "Active" : val === 0 || val === false ? "Inactive" : val;
          } else if (column.dataIndex === "refPaid") {
            val = val === 1 || val === true ? "Paid" : "Unpaid";
          } else if (["at", "date", "on", "created", "updated"].some((x) => k.includes(x))) {
            val = val && moment(val).isValid() ? moment(val).format(isTimeField ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD") : val;
          } else if (typeof val === "boolean") {
            val = val ? "Yes" : "No";
          }
          row[column.title] = val !== undefined && val !== null ? val : "";
        });
      return row;
    });
    return csvData;
  };

  // Add a function to export data to PDF format
  const exportToPdf = () => {
    const doc = new jsPDF();
    const exportCols = columns.filter((col) => col.key !== "action" && col.title !== "Action" && col.dataIndex);
    const tableData = [];
    exportCols.forEach((column) => {
      tableData.push(column.title);
    });
    filteredData.forEach((item) => {
      const rowData = [];
      exportCols.forEach((column) => {
        let val = item[column.dataIndex];
        const k = column.dataIndex?.toLowerCase() || "";
        const isTimeField = k.includes("createdat") || k.includes("updatedat") || k.includes("time");
        if (column.dataIndex === "status") {
          val = val === 1 || val === true ? "Active" : val === 0 || val === false ? "Inactive" : val;
        } else if (column.dataIndex === "refPaid") {
          val = val === 1 || val === true ? "Paid" : "Unpaid";
        } else if (["at", "date", "on", "created", "updated"].some((x) => k.includes(x))) {
          val = val && moment(val).isValid() ? moment(val).format(isTimeField ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD") : val;
        } else if (typeof val === "boolean") {
          val = val ? "Yes" : "No";
        }
        rowData.push(val !== undefined && val !== null ? String(val) : "");
      });
      tableData.push(rowData);
    });

    doc.autoTable({
      head: [tableData.slice(0, exportCols.length)],
      body: tableData.slice(exportCols.length),
    });
    const date = new Date();
    const formattedDate = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    doc.save(`${formattedDate}.pdf`);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData.map(record => 
      columns
        .filter((col) => col.key !== "action" && col.title !== "Action" && col.dataIndex)
        .reduce((obj, column) => {
          let val = record[column.dataIndex];
          const k = column.dataIndex?.toLowerCase() || "";
          const isTimeField = k.includes("createdat") || k.includes("updatedat") || k.includes("time");
          if (column.dataIndex === "status") {
            val = val === 1 || val === true ? "Active" : val === 0 || val === false ? "Inactive" : val;
          } else if (column.dataIndex === "refPaid") {
            val = val === 1 || val === true ? "Paid" : "Unpaid";
          } else if (["at", "date", "on", "created", "updated"].some((x) => k.includes(x))) {
            val = val && moment(val).isValid() ? moment(val).format(isTimeField ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD") : val;
          } else if (typeof val === "boolean") {
            val = val ? "Yes" : "No";
          }
          obj[column.title] = val !== undefined && val !== null ? val : "";
          return obj;
        }, {})
    ));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
    const date = new Date();
    const formattedDate = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    FileSaver.saveAs(data, `${formattedDate}.xlsx`);
  };

  const updatedColumns = columns.map((column) => ({
    ...column,
    title: sumColumns.includes(column.dataIndex) && columnTotals[column.dataIndex]
      ? `${column.title} (Total: ${columnTotals[column.dataIndex]})`
      : column.title,
  }));

  return (
    <div {...rest}>
      <Row gutter={[16, 16]}>
     
      <Col xs={24} sm={12} md={18} lg={18} xl={18} xxl={18}></Col>
      
      <Col xs={24} sm={12} md={6} lg={6} xl={6} xxl={6}>
        <Input
          placeholder="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          suffix={<SearchOutlined />}
          style={{ width: "100%", float: "right"}}
        />
      </Col>
      <Col xs={24}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button type="primary" style={{ marginRight: "16px", background: orderFilter === 'All' ? "#eb2f96" : "transparent", color: orderFilter === 'All' ? "white" : "#eb2f96", borderColor: "#eb2f96"  }} onClick={() => setOrderFilter('All')}>
              All
            </Button>
            <Button type="primary" style={{ marginRight: "16px", background: orderFilter === '1' ? "#13c2c2" : "transparent", color: orderFilter === '1' ? "white" : "#13c2c2", borderColor: "#13c2c2" }} onClick={() => setOrderFilter('1')}>
              IN-PROCESS
            </Button>
            <Button type="primary" style={{ marginRight: "16px", background: orderFilter === '0' ? "#faad14" : "transparent", color: orderFilter === '0' ? "white" : "#faad14", borderColor: "#faad14" }} onClick={() => setOrderFilter('0')}>
              PENDING
            </Button>
            <Button type="primary" style={{ marginRight: "16px", background: orderFilter === '2' ? "#f5222d" : "transparent", color: orderFilter === '2' ? "white" : "#f5222d", borderColor: "#f5222d" }} onClick={() => setOrderFilter('2')}>
              CANCEL
            </Button>
            <Button type="primary" style={{ marginRight: "16px", background: orderFilter === '3' ? "#52c41a" : "transparent", color: orderFilter === '3' ? "white" : "#52c41a", borderColor: "#52c41a" }} onClick={() => setOrderFilter('3')}>
              COMPLETE
            </Button>
          </div>
          <div>
            <CSVLink data={getCsvData()} filename={"data.csv"}>
              <Button type="primary" style={{ marginRight: "16px" }}>
                CSV
              </Button>
            </CSVLink>
            <Button type="primary" onClick={exportToExcel} style={{ marginRight: "16px" }}>
              Excel
            </Button>
            <Button type="primary" onClick={exportToPdf}>
              PDF
            </Button>
          </div>
        </div>
      </Col>
    </Row>

      <Row>
        <Col className="gutter-row" span={24} style={{overflow:"auto", marginTop:"10px"}}>
          <Table
            dataSource={currentRecords}
            columns={updatedColumns}
            pagination={false}
            rowKey={(record) => record.key}
            exportable
            exportableProps={{
              showColumnPicker: true,
            }}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col className="gutter-row" span={12}>
          <span
            style={{ margin: 5 }}
          >{`Showing ${pageData?.pageStart}-${pageData?.pageEnd} of ${filteredData.length} entries`}</span>
        </Col>
        <Col className="gutter-row" span={12}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredData.length}
            onChange={handlePageChange}
            // showTotal={(total) => `Total ${total} entries`}
            showSizeChanger={false}
            style={{ margin: 5, float: "right" }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default DataTable;
